import React, { useState, useEffect } from 'react';
import { ChevronDown, MapPin, Clock, Users, Briefcase, Send, Star, ArrowRight } from 'lucide-react';
import axios from "axios";

const CareerPage = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]); // fetched jobs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    resume: null,
    coverLetter: "",
    schoolName: "",
    schoolCity: "",
    collegeName: "",
    degree: "",
    collegeCity: "",
    postGraduate: "",
    internships: [],
    fullTimeJobs: [],
    linkedinProfile: "",
    portfolioLink: "",
  });

  const [errors, setErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // ✅ Fetch jobs from backend
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get("https://archpoint.onrender.com/api/positions"); 
        setJobs(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to load positions");
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  // Close the job modal with the Escape key, and lock page scroll while it's open
  useEffect(() => {
    if (!selectedJob) return;

    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") setSelectedJob(null);
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [selectedJob]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.position.trim()) newErrors.position = "Position is required";
    if (!formData.resume) newErrors.resume = "Resume is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addInternship = () => {
    setFormData((prev) => ({
      ...prev,
      internships: [
        ...prev.internships,
        { company: "", role: "", duration: "" },
      ],
    }));
  };

  const removeInternship = (index) => {
    setFormData((prev) => ({
      ...prev,
      internships: prev.internships.filter((_, i) => i !== index),
    }));
  };

  const updateInternship = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      internships: prev.internships.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      ),
    }));
  };

  const addFullTimeJob = () => {
    setFormData((prev) => ({
      ...prev,
      fullTimeJobs: [
        ...prev.fullTimeJobs,
        { company: "", role: "", duration: "" },
      ],
    }));
  };

  const removeFullTimeJob = (index) => {
    setFormData((prev) => ({
      ...prev,
      fullTimeJobs: prev.fullTimeJobs.filter((_, i) => i !== index),
    }));
  };

  const updateFullTimeJob = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      fullTimeJobs: prev.fullTimeJobs.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      ),
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setFormData((prev) => ({ ...prev, resume: file }));
      if (errors.resume) {
        setErrors((prev) => ({ ...prev, resume: "" }));
      }
    } else {
      alert("Please select a file smaller than 5MB");
    }
  };

  const handleApplyForPosition = (jobTitle) => {
    setFormData((prev) => ({ ...prev, position: jobTitle }));
    setSelectedJob(null);
    document
      .getElementById("application-form")
      .scrollIntoView({ behavior: "smooth" });
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  try {
    const formPayload = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "internships" || key === "fullTimeJobs") {
        formPayload.append(key, JSON.stringify(formData[key]));
      } else if (key === "resume" && formData.resume) {
        formPayload.append("resume", formData.resume);
      } else {
        formPayload.append(key, formData[key]);
      }
    });

    const res = await axios.post(
      "https://archpoint.onrender.com/api/applications",
      formPayload,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    if (res.status === 201) {
      alert("✅ Application submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        position: "",
        resume: null,
        coverLetter: "",
        schoolName: "",
        schoolCity: "",
        collegeName: "",
        degree: "",
        collegeCity: "",
        postGraduate: "",
        internships: [],
        fullTimeJobs: [],
        linkedinProfile: "",
        portfolioLink: "",
      });
      setErrors({});
    }
  } catch (err) {
    console.error("Error submitting application:", err);
    alert("❌ Failed to submit application. Please try again later.");
  }
};
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`relative overflow-hidden bg-[#fdfbf6] transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
        {/* Subtle blueprint-style grid backdrop */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#1c1917 1px, transparent 1px), linear-gradient(90deg, #1c1917 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500" />

        <div className="relative container mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-amber-600 font-semibold text-sm tracking-[0.2em] uppercase mb-5">
              Careers at Arch Point Consultants
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-stone-900 leading-[1.1] mb-6">
              Shape spaces,
              <br />
              shape your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">career</span>.
            </h1>
            <p className="text-lg text-stone-600 max-w-xl leading-relaxed mb-10 font-light">
              We're a Jaipur-based architecture and interior design studio looking for people who care about detail, craft, and building things that last. Explore where you'd fit in.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() =>
                  document.getElementById("positions")?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-7 py-3.5 rounded-lg font-semibold text-sm hover:bg-stone-800 transition-colors duration-300"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() =>
                  document.getElementById("application-form")?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center gap-2 bg-transparent border border-stone-300 text-stone-800 px-7 py-3.5 rounded-lg font-semibold text-sm hover:border-amber-500 hover:text-amber-700 transition-colors duration-300"
              >
                Submit an Application
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Team Section */}
      <section className={`py-20 border-b border-stone-100 transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="container mx-auto px-6 text-center">
          <p className="text-amber-600 font-semibold text-xs tracking-[0.2em] uppercase mb-3">Life at the studio</p>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-8">Our Team &amp; Work</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {["Culture", "About Work", "Office Space"].map((label) => (
              <button
                key={label}
                className="px-5 py-2.5 rounded-full border border-stone-200 text-stone-600 text-sm font-medium hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-all duration-300"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Open Positions */}
      <section
        id="positions"
        className={`py-20 bg-stone-50 transform transition-all duration-1000 delay-500 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-amber-600 font-semibold text-xs tracking-[0.2em] uppercase mb-3">Join us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900">Open Positions</h2>
          </div>

          {loading && <p className="text-center">Loading positions...</p>}
          {error && <p className="text-center text-red-500">{error}</p>}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {jobs.map((job, index) => (
              <div
                key={job.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer border-l-4 border-yellow-500 animate-fade-in"
                style={{ animationDelay: `${index * 200}ms` }}
                onClick={() => setSelectedJob(job)}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 group-hover:text-yellow-600 transition-colors duration-300">
                      {job.title}
                    </h3>
                    <Star className="w-6 h-6 text-yellow-500" />
                  </div>

                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-4 h-4 mr-2 text-yellow-500" />
                    <span className="mr-4">{job.location}</span>
                  </div>

                  <p className="text-gray-600 mb-6 line-clamp-3">
                    {job.description}
                  </p>

                  <button className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      {selectedJob && (
        <div
          className="jobmodal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedJob(null);
          }}
        >
          <style>{`
            .jobmodal-overlay {
              position: fixed;
              top: 0; left: 0; right: 0; bottom: 0;
              background: rgba(20, 15, 5, 0.6);
              backdrop-filter: blur(3px);
              display: flex;
              align-items: flex-start;
              justify-content: center;
              padding: 5vh 16px;
              z-index: 9999;
              overflow-y: auto;
              animation: jobmodalFade 0.2s ease;
            }
            @keyframes jobmodalFade { from { opacity: 0 } to { opacity: 1 } }
            @keyframes jobmodalSlide { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
            .jobmodal-card {
              background: #ffffff;
              border-radius: 20px;
              width: 100%;
              max-width: 880px;
              max-height: 88vh;
              overflow-y: auto;
              position: relative;
              box-shadow: 0 30px 60px -12px rgba(0,0,0,0.35);
              animation: jobmodalSlide 0.28s cubic-bezier(0.22,1,0.36,1);
            }
            .jobmodal-accent {
              height: 6px;
              width: 100%;
              background: linear-gradient(90deg, #facc15, #f59e0b, #ea580c);
            }
            .jobmodal-header {
              position: sticky;
              top: 0;
              background: #ffffff;
              padding: 28px 32px 20px;
              border-bottom: 1px solid #f3e8d2;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
              z-index: 2;
            }
            .jobmodal-badge {
              display: inline-flex; align-items: center; gap: 6px;
              padding: 5px 12px; border-radius: 999px;
              background: #fffbeb; color: #b45309;
              font-size: 12.5px; font-weight: 600;
              border: 1px solid #fde68a;
            }
            .jobmodal-close {
              width: 38px; height: 38px; border-radius: 50%;
              border: 1px solid #eee;
              background: #fafafa;
              display: flex; align-items: center; justify-content: center;
              cursor: pointer; flex-shrink: 0;
              transition: background 0.18s, transform 0.18s;
              font-size: 20px; color: #57534e; line-height: 1;
            }
            .jobmodal-close:hover { background: #fef3c7; color: #b45309; transform: rotate(90deg); }
            .jobmodal-body { padding: 28px 32px 36px; }
            .jobmodal-section-title {
              display: flex; align-items: center; gap: 8px;
              font-size: 15px; font-weight: 700; text-transform: uppercase;
              letter-spacing: 0.04em; color: #1c1917; margin-bottom: 16px;
            }
            .jobmodal-section-title::before {
              content: ''; width: 4px; height: 18px; border-radius: 2px;
              background: linear-gradient(180deg, #facc15, #ea580c);
              display: inline-block;
            }
            .jobmodal-list-item { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
            .jobmodal-dot {
              width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; margin-top: 7px;
              background: linear-gradient(135deg, #facc15, #ea580c);
            }
            .jobmodal-apply-btn {
              display: inline-flex; align-items: center; gap: 8px;
              padding: 14px 40px; border-radius: 12px; border: none;
              background: linear-gradient(90deg, #eab308, #f59e0b, #ea580c);
              color: #fff; font-weight: 700; font-size: 15px;
              cursor: pointer; box-shadow: 0 8px 20px -6px rgba(234,88,12,0.55);
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .jobmodal-apply-btn:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 12px 26px -6px rgba(234,88,12,0.6); }
          `}</style>

          <div className="jobmodal-card">
            <div className="jobmodal-accent" />

            <div className="jobmodal-header">
              <div>
                <span className="jobmodal-badge">
                  <MapPin size={12} /> {selectedJob.location}
                </span>
                <h2 style={{ fontSize: "26px", fontWeight: 800, color: "#1c1917", marginTop: "12px", lineHeight: 1.25 }}>
                  {selectedJob.title}
                </h2>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                aria-label="Close job details"
                className="jobmodal-close"
              >
                ×
              </button>
            </div>

            <div className="jobmodal-body">
              <p style={{ color: "#57534e", fontSize: "15px", lineHeight: 1.75, marginBottom: "32px" }}>
                {selectedJob.description}
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "36px" }} className="jobmodal-grid">
                <div>
                  <div className="jobmodal-section-title">Responsibilities</div>
                  {selectedJob.responsibilities?.map((resp, index) => (
                    <div key={index} className="jobmodal-list-item">
                      <span className="jobmodal-dot" />
                      <span style={{ color: "#44403c", fontSize: "14.5px", lineHeight: 1.65 }}>{resp}</span>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="jobmodal-section-title">Requirements</div>
                  {selectedJob.requirements?.map((req, index) => (
                    <div key={index} className="jobmodal-list-item">
                      <span className="jobmodal-dot" />
                      <span style={{ color: "#44403c", fontSize: "14.5px", lineHeight: 1.65 }}>{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: "36px", textAlign: "center", paddingTop: "28px", borderTop: "1px solid #f3e8d2" }}>
                <button
                  onClick={() => handleApplyForPosition(selectedJob.title)}
                  className="jobmodal-apply-btn"
                >
                  Apply for this Position
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>

          <style>{`
            @media (max-width: 640px) {
              .jobmodal-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
              .jobmodal-header, .jobmodal-body { padding-left: 20px !important; padding-right: 20px !important; }
            }
          `}</style>
        </div>
      )}

      {/* Application Form */}
      <section id="application-form" className="py-16 bg-gradient-to-b from-yellow-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Apply for Position</h2>
              <p className="text-xl text-gray-600">Join our team and help shape amazing spaces</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full p-4 border-2 rounded-lg focus:outline-none transition-all duration-300 ${
                      errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'
                    }`}
                    required
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full p-4 border-2 rounded-lg focus:outline-none transition-all duration-300 ${
                      errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'
                    }`}
                    required
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile No. *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full p-4 border-2 rounded-lg focus:outline-none transition-all duration-300 ${
                      errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'
                    }`}
                    required
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Position Applying For *</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className={`w-full p-4 border-2 rounded-lg focus:outline-none transition-all duration-300 ${
                      errors.position ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'
                    }`}
                    required
                  >
                    <option value="">Select Position</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.title}>
                        {job.title}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {errors.position && <p className="text-red-500 text-sm mt-1">{errors.position}</p>}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Attach CV *</label>
                <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-yellow-500 transition-all duration-300 cursor-pointer ${
                  errors.resume ? 'border-red-500' : 'border-gray-300'
                }`}>
                  <input 
                    type="file" 
                    className="hidden" 
                    id="resume" 
                    accept=".pdf,.doc,.docx" 
                    onChange={handleFileChange}
                  />
                  <label htmlFor="resume" className="cursor-pointer">
                    <Briefcase className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {formData.resume ? formData.resume.name : 'Click to upload your resume'}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">PDF, DOC, DOCX (Max 5MB)</p>
                  </label>
                </div>
                {errors.resume && <p className="text-red-500 text-sm mt-1">{errors.resume}</p>}
              </div>

              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cover Letter (Optional)</label>
                <textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300 resize-none"
                  placeholder="Tell us why you're perfect for this role..."
                ></textarea>
              </div>

              {/* Education Details */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 border-b-2 border-yellow-500 pb-2">Education Details</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">School Name & City:</label>
                    <input
                      type="text"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleInputChange}
                      placeholder="e.g., ABC High School, Mumbai"
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Graduate (College, Degree, City):</label>
                    <input
                      type="text"
                      name="collegeName"
                      value={formData.collegeName}
                      onChange={handleInputChange}
                      placeholder="e.g., XYZ University, B.Arch, Delhi"
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Post Graduate (If Any):</label>
                  <input
                    type="text"
                    name="postGraduate"
                    value={formData.postGraduate}
                    onChange={handleInputChange}
                    placeholder="e.g., Masters in Architecture, IIT Delhi"
                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                  />
                </div>
              </div>

              {/* Experience Details */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 border-b-2 border-yellow-500 pb-2">Experience Details</h3>
                
                {/* Internships */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-700">Internships:</h4>
                    <button
                      type="button"
                      onClick={addInternship}
                      className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-all duration-300 text-sm flex items-center"
                    >
                      + Add Internship
                    </button>
                  </div>

                  {formData.internships.map((internship, index) => (
                    <div key={index} className="grid md:grid-cols-3 gap-4 mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <input
                        type="text"
                        placeholder="Company Name"
                        value={internship.company}
                        onChange={(e) => updateInternship(index, 'company', e.target.value)}
                        className="p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                      />
                      <input
                        type="text"
                        placeholder="Role"
                        value={internship.role}
                        onChange={(e) => updateInternship(index, 'role', e.target.value)}
                        className="p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Duration (e.g., 3 months)"
                          value={internship.duration}
                          onChange={(e) => updateInternship(index, 'duration', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                        />
                        <button
                          type="button"
                          onClick={() => removeInternship(index)}
                          className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-all duration-300 text-sm"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Full Time Jobs */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-700">Full Time:</h4>
                    <button
                      type="button"
                      onClick={addFullTimeJob}
                      className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-all duration-300 text-sm flex items-center"
                    >
                      + Add Job
                    </button>
                  </div>

                  {formData.fullTimeJobs.map((job, index) => (
                    <div key={index} className="grid md:grid-cols-3 gap-4 mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <input
                        type="text"
                        placeholder="Company Name"
                        value={job.company}
                        onChange={(e) => updateFullTimeJob(index, 'company', e.target.value)}
                        className="p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                      />
                      <input
                        type="text"
                        placeholder="Role"
                        value={job.role}
                        onChange={(e) => updateFullTimeJob(index, 'role', e.target.value)}
                        className="p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Duration (e.g., 2 years)"
                          value={job.duration}
                          onChange={(e) => updateFullTimeJob(index, 'duration', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                        />
                        <button
                          type="button"
                          onClick={() => removeFullTimeJob(index)}
                          className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-all duration-300 text-sm"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Profile Links */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 border-b-2 border-yellow-500 pb-2">Profile Links</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn Profile Link:</label>
                    <input
                      type="url"
                      name="linkedinProfile"
                      value={formData.linkedinProfile}
                      onChange={handleInputChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Portfolio Link:</label>
                    <input
                      type="url"
                      name="portfolioLink"
                      value={formData.portfolioLink}
                      onChange={handleInputChange}
                      placeholder="https://yourportfolio.com"
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none transition-all duration-300"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white py-4 px-8 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
              >
                Submit Application
                <Send className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-gradient-to-r from-gray-800 via-gray-900 to-black text-white">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-8">STAY UPDATED WITH OUR NEWSLETTER</h2>
            <div className="flex flex-col md:flex-row justify-center items-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your Name"
                className="w-full md:w-auto flex-1 p-4 rounded-l-lg md:rounded-r-none rounded-r-lg border-none focus:outline-none text-gray-800 mb-4 md:mb-0"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full md:w-auto flex-1 p-4 border-none focus:outline-none text-gray-800 mb-4 md:mb-0"
              />
              <button className="w-full md:w-auto bg-gradient-to-r from-yellow-500 to-amber-500 text-white py-4 px-8 rounded-r-lg md:rounded-l-none rounded-l-lg font-semibold hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 transform hover:scale-105">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default CareerPage;